import React from 'react';
import { formatDate } from '@repo/util/formatDate';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { COMPACT_TABLE_MEDIA_QUERY, PHONE_TABLE_MEDIA_QUERY } from '../Table/compactLayout';

/**
 * Compact message-table cells. On laptop and desktop widths the message tables
 * merge related columns (status + error, all timestamps) and size the rest
 * tightly so the table fits without sideways scrolling. Narrower screens keep
 * the full column set and scroll.
 */
export const COMPACT_MESSAGES_MEDIA_QUERY = COMPACT_TABLE_MEDIA_QUERY;
/** Phones show the table as cards; they use the compact columns too. */
export const MOBILE_MESSAGES_MEDIA_QUERY = PHONE_TABLE_MEDIA_QUERY;
/** Tables that also show an Account column need a little more room. */
export const COMPACT_MESSAGES_WITH_ACCOUNT_MEDIA_QUERY = '(min-width: 1440px)';

/** Fixed widths for the compact columns; together they fit a 1280px laptop. */
export const COMPACT_MESSAGE_COLUMN_WIDTHS = {
  status: 125,
  service: 72,
  direction: 100,
  number: 135,
  lastStatus: 195,
  actions: 155,
} as const;

type Timestamp = string | Date | null | undefined;

export const MessageStatusDot = ({
  status,
  errorCode,
}: {
  status: string | null | undefined;
  errorCode: string | null | undefined;
}) => (
  <span
    className={`inline-block shrink-0 h-2.5 w-2.5 rounded-full ${
      status === 'UNDELIVERED'
        ? 'bg-yellow-500'
        : errorCode === 'OK'
          ? 'bg-green-500'
          : 'bg-red-500'
    }`}
  />
);

/**
 * Status with an info icon beside it when the message has an error; hovering
 * or focusing the icon shows the error code. Replaces the separate Error column.
 */
export const MessageStatusCell = ({
  status,
  errorCode,
}: {
  status: string | null | undefined;
  errorCode: string | null | undefined;
}) => {
  const hasError = !!errorCode && errorCode !== 'OK';
  return (
    <div className="flex gap-2 justify-end items-center min-w-0 md:justify-start">
      <MessageStatusDot status={status} errorCode={errorCode} />
      <span>{status}</span>
      {hasError && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Error: ${errorCode}`}
              className="flex items-center leading-none text-red-700 cursor-help dark:text-red-300"
            >
              <span aria-hidden className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                info
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-[300px] font-mono text-xs break-all bg-popover text-popover-foreground border border-border shadow-md">
            {errorCode}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

const DIRECTION_LABELS: Record<string, string> = { INBOUND: 'Inbound', OUTBOUND: 'Outbound' };

/**
 * Who sent and who received the message. Outbound messages go from the host
 * (account) number to the remote number; inbound messages go the other way.
 */
export const messageParties = (
  hostNumber: string | null | undefined,
  remoteNumber: string | null | undefined,
  direction: string | null | undefined
) =>
  direction === 'INBOUND'
    ? { from: remoteNumber, to: hostNumber }
    : { from: hostNumber, to: remoteNumber };

export const MessageDirectionCell = ({ direction }: { direction: string | null | undefined }) => (
  <span>{direction ? (DIRECTION_LABELS[direction] ?? direction) : '-'}</span>
);

/**
 * The latest status events with their times (newest first), replacing the
 * Created at / Sent at / Delivered at columns. Hover shows every event.
 */
export const MessageLastStatusCell = ({
  insertedAt,
  processedAt,
  deliveredAt,
  shown = 2,
}: {
  insertedAt: Timestamp;
  processedAt: Timestamp;
  deliveredAt: Timestamp;
  /** How many of the newest events to show; the rest are in the hover title. */
  shown?: number;
}) => {
  const candidates: [string, Timestamp][] = [
    ['Delivered', deliveredAt],
    ['Sent', processedAt],
    ['Created', insertedAt],
  ];
  const events: [string, string][] = [];
  for (const [label, value] of candidates) {
    const formatted = value ? formatDate(value as string | Date) : undefined;
    if (formatted) events.push([label, formatted]);
  }

  if (events.length === 0) return <span>-</span>;

  // Each event stacks its label over its timestamp; the newest is emphasised
  return (
    <div
      className="flex flex-col gap-1.5 items-end text-right md:items-start md:text-left"
      title={events.map(([label, value]) => `${label}: ${value}`).join('\n')}
    >
      {events.slice(0, shown).map(([label, value], i) => (
        <div key={label} className="flex flex-col leading-tight">
          <span className={i === 0 ? 'font-bold' : 'text-xs text-grey-600 dark:text-grey-400'}>
            {label}
          </span>
          <span
            className={`whitespace-nowrap ${i === 0 ? '' : 'text-xs text-grey-600 dark:text-grey-400'}`}
          >
            {value}
          </span>
        </div>
      ))}
    </div>
  );
};
